import { useState } from 'react';

import type { MouseEvent } from 'react';

import { TRAINER_INVITATION_STATUS } from '@treneo/features/organizations';
import type { ISelectItem, ITrainerInvitationDto } from '@treneo/models';
import { useAppFormContext, useAppWatch } from '@treneo/shared/forms';
import { useAppTranslations } from '@treneo/shared/i18n';
import { CloseIcon, ExpandMoreIcon, IconSizes } from '@treneo/shared/icons';
import {
  Box,
  Collapse,
  Column,
  IconButton,
  toast,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@treneo/shared/ui';
import { extractRtkQueryErrorMessages } from '@treneo/shared/utils';

import {
  useCancelCourseTrainerInvitationMutation,
  useCourseTrainerInvitationsQuery,
  useInviteTrainerToCourseMutation,
  useOrganizationTrainersByDaDesktopIdQuery
} from '../../../../data';

import { courseFormFieldNames } from '../course-form-field-names';

import { CourseInvitationRow } from './course-invitation-row';
import { InviteTrainerByEmail } from './invite-trainer-by-email';
import { TrainerSelect } from './trainer-select';

const TRAINER_MODE_SELECT = 'select';
const TRAINER_MODE_INVITE = 'invite';

type TrainerMode = typeof TRAINER_MODE_SELECT | typeof TRAINER_MODE_INVITE;

type Props = Readonly<{
  isEdit: boolean;
  courseId?: string;
  allowImmediateInvite?: boolean;
  initialOrganizationId?: number;
  organizationTrainers?: ISelectItem[];
  organizationTrainersLoading?: boolean;
  assignedTrainerFromCourse?: { id: string; label: string } | null;
}>;

export const TrainerSelectSectionWithQuery = ({
  isEdit,
  courseId,
  initialOrganizationId,
  assignedTrainerFromCourse
}: Readonly<
  Pick<Props, 'isEdit' | 'courseId' | 'initialOrganizationId' | 'assignedTrainerFromCourse'>
>) => {
  const { control } = useAppFormContext();
  const organizationIdFromForm = useAppWatch({
    control,
    name: courseFormFieldNames.daOrganizationId
  }) as number | null | undefined;
  const effectiveOrganizationId = organizationIdFromForm ?? initialOrganizationId ?? 0;
  const query = useOrganizationTrainersByDaDesktopIdQuery(effectiveOrganizationId, {
    enabled: effectiveOrganizationId > 0
  });
  const organizationTrainers = (query.data?.trainers ?? []) as ISelectItem[];
  const organizationTrainersLoading = query.isLoading;
  const allowImmediateInvite = isEdit && !!courseId;

  return (
    <TrainerSelectSection
      isEdit={isEdit}
      courseId={courseId}
      allowImmediateInvite={allowImmediateInvite}
      initialOrganizationId={initialOrganizationId}
      organizationTrainers={organizationTrainers}
      organizationTrainersLoading={organizationTrainersLoading}
      assignedTrainerFromCourse={assignedTrainerFromCourse}
    />
  );
};

export const TrainerSelectSection = ({
  isEdit,
  courseId,
  allowImmediateInvite = false,
  initialOrganizationId,
  organizationTrainers: organizationTrainersProp,
  organizationTrainersLoading: organizationTrainersLoadingProp,
  assignedTrainerFromCourse
}: Props) => {
  const { t } = useAppTranslations();
  const { control, setValue } = useAppFormContext();
  const organizationIdFromForm = useAppWatch({
    control,
    name: courseFormFieldNames.daOrganizationId
  }) as number | null | undefined;
  const trainerEmail = useAppWatch({
    control,
    name: courseFormFieldNames.trainerEmail
  }) as string | null | undefined;
  const watchedPending = useAppWatch({
    control,
    name: courseFormFieldNames.pendingTrainerInviteEmails
  });
  const pendingTrainerInviteEmails = Array.isArray(watchedPending) ? watchedPending : [];

  const invitationsQuery = useCourseTrainerInvitationsQuery({
    courseId: courseId ?? '',
    enabled: isEdit && !!courseId
  });
  const inviteTrainerMutation = useInviteTrainerToCourseMutation();
  const cancelInvitationMutation = useCancelCourseTrainerInvitationMutation();
  const invitations = invitationsQuery.data?.invitations ?? [];

  const effectiveOrganizationId = organizationIdFromForm ?? initialOrganizationId ?? 0;
  const baseOrgTrainers = organizationTrainersProp ?? [];
  const selectedTrainerId = useAppWatch({
    control,
    name: courseFormFieldNames.trainerUserId
  }) as string | null | undefined;

  let selectItems: ISelectItem[] = baseOrgTrainers;
  if (
    selectedTrainerId != null &&
    assignedTrainerFromCourse?.id === selectedTrainerId &&
    !baseOrgTrainers.some(item => item.id === selectedTrainerId)
  ) {
    selectItems = [
      {
        id: assignedTrainerFromCourse.id,
        label: assignedTrainerFromCourse.label
      },
      ...baseOrgTrainers
    ];
  }

  const isLoadingTrainers = organizationTrainersLoadingProp ?? false;
  const selectedTrainer = selectItems.find(item => item.id === selectedTrainerId) ?? null;
  const pendingInvitations = invitations.filter(
    inv => Number(inv.status) === TRAINER_INVITATION_STATUS.PENDING
  );
  const otherInvitations = invitations.filter(
    inv => Number(inv.status) !== TRAINER_INVITATION_STATUS.PENDING
  );
  const invitationsList = [
    ...pendingInvitations,
    ...otherInvitations
  ] as unknown as ITrainerInvitationDto[];

  const [trainerMode, setTrainerMode] = useState<TrainerMode>(TRAINER_MODE_SELECT);

  const invitationForEmail = trainerEmail
    ? invitations.find(inv => inv.email.toLowerCase() === trainerEmail.toLowerCase())
    : null;
  const hasPendingForEmailFromApi =
    invitationForEmail != null &&
    Number(invitationForEmail.status) === TRAINER_INVITATION_STATUS.PENDING;
  const isEmailInPendingList =
    !!trainerEmail &&
    pendingTrainerInviteEmails.some(
      e => e.trim().toLowerCase() === trainerEmail.trim().toLowerCase()
    );
  const hasPendingForEmail = isEdit ? hasPendingForEmailFromApi : isEmailInPendingList;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [invitationsSectionExpanded, setInvitationsSectionExpanded] = useState(false);

  const handleTrainerChange = (trainer: ISelectItem | null) => {
    setValue(courseFormFieldNames.trainerUserId, trainer?.id ?? null, {
      shouldValidate: false
    });
    if (trainer) {
      setValue(courseFormFieldNames.trainerEmail, '', { shouldValidate: false });
    }
  };

  const handleInviteTrainerByEmail = async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed || effectiveOrganizationId <= 0) {
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      toast.error(t('trainers.invalidEmail'));
      return;
    }

    if (allowImmediateInvite && courseId) {
      try {
        await inviteTrainerMutation.mutateAsync({
          courseId,
          email: trimmed,
          origin: globalThis.location.origin
        });
        toast.success(t('trainers.invitationSent'));
        setValue(courseFormFieldNames.trainerEmail, '', { shouldValidate: false });
        setValue(
          courseFormFieldNames.pendingTrainerInviteEmails,
          [...pendingTrainerInviteEmails, trimmed],
          { shouldValidate: false }
        );
      } catch (error: unknown) {
        const messages = extractRtkQueryErrorMessages(error, 'trainers.invitationFailed');
        const toShow = messages.length > 0 ? messages : ['trainers.invitationFailed'];
        const display = toShow.map(key => t(key)).join('\n');
        toast.error(display);
      }
      return;
    }
    const normalized = trimmed.toLowerCase();
    if (pendingTrainerInviteEmails.some(e => e.trim().toLowerCase() === normalized)) {
      toast.error(t('trainers.invitationAlreadyPending'));
      return;
    }
    setValue(
      courseFormFieldNames.pendingTrainerInviteEmails,
      [...pendingTrainerInviteEmails, trimmed],
      {
        shouldValidate: false
      }
    );
    setValue(courseFormFieldNames.trainerEmail, '', { shouldValidate: false });
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!courseId) {
      return;
    }
    try {
      await cancelInvitationMutation.mutateAsync({ courseId, invitationId });
      toast.success(t('trainers.invitationCancelled'));
    } catch (error: unknown) {
      const messages = extractRtkQueryErrorMessages(error, 'trainers.invitationCancelFailed');
      const toShow = messages.length > 0 ? messages : ['trainers.invitationCancelFailed'];
      const display = toShow.map(key => t(key)).join('\n');
      toast.error(display);
    }
  };

  const handleToggleDetails = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRemovePendingInvite = (emailToRemove: string) => {
    const next = pendingTrainerInviteEmails.filter(
      e => e.trim().toLowerCase() !== emailToRemove.trim().toLowerCase()
    );
    setValue(courseFormFieldNames.pendingTrainerInviteEmails, next, {
      shouldValidate: false
    });
  };

  const isInvitePending = isEdit && inviteTrainerMutation.isPending;

  const handleModeChange = (_: MouseEvent<HTMLElement>, newMode: TrainerMode | null) => {
    if (newMode != null) {
      setTrainerMode(newMode);
    }
  };

  return (
    <Column spacing={2}>
      <Box>
        <ToggleButtonGroup
          value={trainerMode}
          exclusive
          onChange={handleModeChange}
          aria-label={t('trainers.trainerAssignmentMode')}
          size='small'
          sx={{ mb: 1.5 }}
        >
          <ToggleButton value={TRAINER_MODE_SELECT} aria-label={t('trainers.modeSelectFromList')}>
            {t('trainers.modeSelectFromList')}
          </ToggleButton>
          <ToggleButton value={TRAINER_MODE_INVITE} aria-label={t('trainers.modeInviteByEmail')}>
            {t('trainers.modeInviteByEmail')}
          </ToggleButton>
        </ToggleButtonGroup>

        {trainerMode === TRAINER_MODE_SELECT && (
          <>
            <TrainerSelect
              organizationId={effectiveOrganizationId}
              value={selectedTrainer}
              onChange={handleTrainerChange}
              trainers={selectItems}
              isLoadingTrainers={isLoadingTrainers}
            />
            <Typography variant='caption' color='text.secondary' component='p' sx={{ mt: 0.5 }}>
              {t('trainers.hintSwitchToInvite')}
            </Typography>
          </>
        )}

        {trainerMode === TRAINER_MODE_INVITE && (
          <InviteTrainerByEmail
            trainerEmail={trainerEmail}
            isInvitePending={isInvitePending}
            hasPendingForEmail={hasPendingForEmail}
            onInvite={() => {
              const email = (trainerEmail ?? '').trim();
              if (email) {
                handleInviteTrainerByEmail(email);
              }
            }}
          />
        )}
      </Box>

      {isEdit && courseId && invitationsList.length > 0 && (
        <Box>
          <Box
            role='button'
            tabIndex={0}
            onClick={() => {
              setInvitationsSectionExpanded(prev => !prev);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setInvitationsSectionExpanded(prev => !prev);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              width: '100%',
              padding: 0,
              marginBottom: invitationsSectionExpanded ? 1 : 0,
              cursor: 'pointer',
              textAlign: 'left',
              userSelect: 'none'
            }}
            aria-expanded={invitationsSectionExpanded ? 'true' : 'false'}
            aria-label={
              invitationsSectionExpanded
                ? t('trainers.hideDetails', { defaultValue: 'Hide details' })
                : t('trainers.showDetails', { defaultValue: 'Show details' })
            }
          >
            <Typography variant='body2' color='text.secondary' component='span'>
              {t('trainers.courseInvitations')}
            </Typography>
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                transform: invitationsSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
              aria-hidden
            >
              <ExpandMoreIcon fontSize={IconSizes.small} />
            </Box>
          </Box>
          <Collapse in={invitationsSectionExpanded}>
            <Column spacing={1}>
              {invitationsList.map(inv => (
                <CourseInvitationRow
                  key={String(inv.id)}
                  invitation={inv}
                  isExpanded={expandedIds.has(String(inv.id))}
                  onToggleDetails={() => handleToggleDetails(String(inv.id))}
                  onCancel={() => handleCancelInvitation(String(inv.id))}
                  isCancelPending={cancelInvitationMutation.isPending}
                />
              ))}
            </Column>
          </Collapse>
        </Box>
      )}

      {!isEdit && pendingTrainerInviteEmails.length > 0 && (
        <Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {t('trainers.toBeInvitedAfterCreate', {
              defaultValue: 'Invitations will be sent when you save the course'
            })}
          </Typography>
          <Column spacing={0.5}>
            {pendingTrainerInviteEmails.map((email: string) => (
              <Box
                key={email}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover'
                }}
              >
                <Typography variant='body2' component='span'>
                  {email}
                </Typography>
                <IconButton
                  size='small'
                  onClick={() => handleRemovePendingInvite(email)}
                  aria-label={t('trainers.removeFromList', { defaultValue: 'Remove from list' })}
                  color='error'
                >
                  <CloseIcon fontSize={IconSizes.small} />
                </IconButton>
              </Box>
            ))}
          </Column>
        </Box>
      )}
    </Column>
  );
};
